import React, { useEffect, useMemo, useState } from 'react';
import { getCompareAssetCardData } from '../../util/AssetStatusCard.util';
import CompareAssetCardBarChart from '../CompareAssetCardBarChart';
import CompareAssetCardAccordion from '../CompareAssetCardAccordion';
import { useAppStateMonthCompareModalDataStore, useAppStateOwnerFilterStore } from '../../../../common/store/AppStateStore.zustand';
import { AssetReport, CompareAssetCardData, InvestmentItemCompareData, ReportItemCompareData } from '../../type/AssetReport.type';

const yearMonthToTotalMonths = (yearMonth: string): number => {
  const [year, month] = yearMonth.split('-').map(Number);
  return year * 12 + month;
};

const formatYearMonth = (yearMonth: string): string => {
  const [year, month] = yearMonth.split('-').map(Number);
  return `${year}년 ${month}월`;
};

const aggregateCompareDataList = (dataList: CompareAssetCardData[]): CompareAssetCardData => {
  const savingMap = new Map<string, ReportItemCompareData>();
  const investmentMap = new Map<string, InvestmentItemCompareData>();
  const etcMap = new Map<string, ReportItemCompareData>();

  for (const data of dataList) {
    for (const item of data.compareSavingItemList) {
      const existing = savingMap.get(item.title);
      if (existing) {
        existing.changeAmount += item.changeAmount;
      } else {
        savingMap.set(item.title, { ...item });
      }
    }
    for (const item of data.compareInvestmentItemList) {
      const existing = investmentMap.get(item.title);
      if (existing) {
        existing.changeAmount += item.changeAmount;
        existing.changePrincipalAmount += item.changePrincipalAmount;
      } else {
        investmentMap.set(item.title, { ...item });
      }
    }
    for (const item of data.compareEtcItemList) {
      const existing = etcMap.get(item.title);
      if (existing) {
        existing.changeAmount += item.changeAmount;
      } else {
        etcMap.set(item.title, { ...item });
      }
    }
  }

  const compareSavingItemList = Array.from(savingMap.values()).sort((a, b) => b.changeAmount - a.changeAmount);
  const compareInvestmentItemList = Array.from(investmentMap.values()).sort((a, b) => b.changeAmount - a.changeAmount);
  const compareEtcItemList = Array.from(etcMap.values()).sort((a, b) => b.changeAmount - a.changeAmount);

  const savingChangeAmount = dataList.reduce((sum, d) => sum + d.savingChangeAmount, 0);
  const investmentChangeAmount = dataList.reduce((sum, d) => sum + d.investmentChangeAmount, 0);
  const investmentChangePrincipalAmount = dataList.reduce((sum, d) => sum + d.investmentChangePrincipalAmount, 0);
  const totalChangeAmount = dataList.reduce((sum, d) => sum + d.totalChangeAmount, 0);

  return {
    totalChangeAmount,
    savingChangeAmount,
    investmentChangeAmount,
    investmentChangePrincipalAmount,
    investmentChangeProfitAmount: investmentChangeAmount - investmentChangePrincipalAmount,
    etcChangeAmount: totalChangeAmount - savingChangeAmount - investmentChangeAmount,
    compareSavingItemList,
    compareInvestmentItemList,
    compareEtcItemList,
  };
};

const YearSettlementModal: React.FC = () => {
  const { monthCompareModalData } = useAppStateMonthCompareModalDataStore();
  const { ownerFilter } = useAppStateOwnerFilterStore();
  const [baseYearMonth, setBaseYearMonth] = useState<string>('');
  const [compareYearMonth, setCompareYearMonth] = useState<string>('');

  // 모달 열릴 때 초기값 설정: 기준 = 현재 선택 월, 비교 = 그 다음 월
  useEffect(() => {
    if (!monthCompareModalData?.assetReportList?.length) return;

    const { currentReport, assetReportList } = monthCompareModalData;
    const initialBase = currentReport.yearMonth;
    const initialCompare = assetReportList.find((r) => r.yearMonth !== initialBase)?.yearMonth ?? '';

    setBaseYearMonth(initialBase);
    setCompareYearMonth(initialCompare);
  }, [monthCompareModalData]);

  const handleBaseChange = (yearMonth: string) => {
    setBaseYearMonth(yearMonth);
    // 기준 월과 비교 월이 같아지면 비교 월을 다른 것으로 교체
    if (compareYearMonth === yearMonth) {
      const other = monthCompareModalData?.assetReportList.find((r) => r.yearMonth !== yearMonth)?.yearMonth ?? '';
      setCompareYearMonth(other);
    }
  };

  const handleCompareChange = (yearMonth: string) => {
    setCompareYearMonth(yearMonth);
    // 비교 월과 기준 월이 같아지면 기준 월을 다른 것으로 교체
    if (baseYearMonth === yearMonth) {
      const other = monthCompareModalData?.assetReportList.find((r) => r.yearMonth !== yearMonth)?.yearMonth ?? '';
      setBaseYearMonth(other);
    }
  };

  const monthCompareData = useMemo(() => {
    if (!monthCompareModalData?.assetReportList || !baseYearMonth || !compareYearMonth) return null;

    const { assetReportList } = monthCompareModalData;

    const baseTotal = yearMonthToTotalMonths(baseYearMonth);
    const compareTotal = yearMonthToTotalMonths(compareYearMonth);
    const newerTotal = Math.max(baseTotal, compareTotal);
    const olderTotal = Math.min(baseTotal, compareTotal);

    // 두 선택 월 사이(포함)의 레포트를 날짜 내림차순 정렬
    const betweenReports: AssetReport[] = assetReportList
      .filter((r) => {
        const t = yearMonthToTotalMonths(r.yearMonth);
        return t >= olderTotal && t <= newerTotal;
      })
      .sort((a, b) => yearMonthToTotalMonths(b.yearMonth) - yearMonthToTotalMonths(a.yearMonth));

    // 오너 필터 적용
    const filteredReports = ownerFilter
      ? betweenReports.map((r) => ({
          ...r,
          reportItemList: r.reportItemList.filter((item) => item.owner === ownerFilter),
        }))
      : betweenReports;

    if (filteredReports.length < 2) return null;

    // 인접 쌍별 비교 데이터 계산
    const pairCompareDataList = [];
    for (let i = 0; i < filteredReports.length - 1; i++) {
      const newerReport = filteredReports[i];
      const olderReport = filteredReports[i + 1];
      pairCompareDataList.push({
        newerYearMonth: newerReport.yearMonth,
        olderYearMonth: olderReport.yearMonth,
        compareData: getCompareAssetCardData(newerReport, olderReport),
      });
    }

    // 전체 합산: 각 월별 비교 데이터를 항목별로 합산
    const summaryData = aggregateCompareDataList(pairCompareDataList.map((p) => p.compareData));

    return { summaryData, pairCompareDataList };
  }, [monthCompareModalData?.assetReportList, baseYearMonth, compareYearMonth, ownerFilter]);

  if (!monthCompareModalData?.isOpen) return null;

  const { assetReportList, onClose } = monthCompareModalData;
  const isMultiMonth = (monthCompareData?.pairCompareDataList.length ?? 0) > 1;

  const selectClass =
    'appearance-none bg-white border border-gray-200 rounded-lg py-1.5 pl-3 pr-8 text-gray-700 shadow-sm hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 cursor-pointer !rounded-button text-sm transition-all duration-200';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold text-gray-800">월별 비교</h2>
              {ownerFilter && (
                <span className="bg-blue-100 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full">
                  {ownerFilter === 'HUSBAND' ? '남편' : '아내'}
                </span>
              )}
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition-colors">
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="flex items-center flex-wrap gap-3 mt-3">
            <span className="text-gray-500 text-sm">기준 월</span>
            <div className="relative">
              <select value={baseYearMonth} onChange={(e) => handleBaseChange(e.target.value)} className={selectClass}>
                {assetReportList.map((r) => (
                  <option key={r.id} value={r.yearMonth}>
                    {r.yearMonth}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                <i className="fas fa-chevron-down text-xs"></i>
              </div>
            </div>
            <span className="text-gray-400 text-sm">vs</span>
            <span className="text-gray-500 text-sm">비교 월</span>
            <div className="relative">
              <select value={compareYearMonth} onChange={(e) => handleCompareChange(e.target.value)} className={selectClass}>
                {assetReportList
                  .filter((r) => r.yearMonth !== baseYearMonth)
                  .map((r) => (
                    <option key={r.id} value={r.yearMonth}>
                      {r.yearMonth}
                    </option>
                  ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                <i className="fas fa-chevron-down text-xs"></i>
              </div>
            </div>
          </div>
        </div>
        <div className="p-6">
          {!monthCompareData && (
            <div className="flex flex-col items-center justify-center h-64 bg-gray-50/50 rounded-lg p-6">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
                <i className="fas fa-wallet text-3xl text-green-300"></i>
              </div>
              <h4 className="text-lg font-medium text-gray-700 mb-2">비교 데이터가 없습니다</h4>
              <p className="text-sm text-gray-500 text-center max-w-sm">비교할 월을 선택해주세요</p>
            </div>
          )}
          {monthCompareData && (
            <>
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">자산 변동 요약</h3>
                <CompareAssetCardBarChart {...monthCompareData.summaryData} />
              </div>
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  {isMultiMonth ? '전체 합산 내역' : '상세 내역'}
                </h3>
                <CompareAssetCardAccordion {...monthCompareData.summaryData} />
              </div>
              {isMultiMonth && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">월별 상세 내역</h3>
                  <div className="space-y-4">
                    {monthCompareData.pairCompareDataList.map((pairData) => (
                      <div
                        key={`${pairData.newerYearMonth}-${pairData.olderYearMonth}`}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <h4 className="text-base font-semibold text-gray-700 mb-3">
                          {formatYearMonth(pairData.newerYearMonth)} vs {formatYearMonth(pairData.olderYearMonth)}
                        </h4>
                        <CompareAssetCardAccordion {...pairData.compareData} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <div className="p-6 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-all duration-200 !rounded-button"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default YearSettlementModal;
